import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Briefcase, ShieldCheck, GraduationCap } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import usePageMeta from "../../lib/usePageMeta";

const humanize = (v) => (v ? String(v).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "");

export default function PublicCareers() {
  usePageMeta({
    title: "Careers | California Corporate Chaplains",
    description:
      "Explore employment opportunities with California Corporate Chaplains — compassionate, professional support for individuals, employees, families, and organizations.",
  });
  const [jobs, setJobs] = useState(null);

  useEffect(() => {
    api.get("/careers")
      .then((r) => setJobs(r.data))
      .catch((err) => {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        setJobs([]);
      });
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20" data-testid="careers-page">
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Careers</p>
      <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
        Employment Opportunities
      </h1>
      <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
        Explore opportunities to serve with California Corporate Chaplains. We are committed to compassionate, professional support for individuals, employees, families, and organizations.
      </p>

      <div className="mt-12">
        {jobs === null ? (
          <div className="py-16 text-center text-slate-500 text-sm">Loading opportunities…</div>
        ) : jobs.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-10 text-center" data-testid="careers-empty">
            <ShieldCheck className="w-6 h-6 text-[#4A6FA5] mx-auto mb-3" strokeWidth={1.75} />
            <p className="font-heading text-xl text-slate-900">There are no current openings at this time.</p>
            <p className="mt-2 text-slate-600">Please check back for future opportunities.</p>
          </div>
        ) : (
          <ul className="space-y-4" data-testid="careers-list">
            {jobs.map((j) => (
              <li key={j.id}>
                <Link
                  to={`/careers/${j.slug}`}
                  data-testid={`career-card-${j.slug}`}
                  className="group block rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#4A6FA5] hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      {j.is_upcoming && (
                        <span data-testid={`upcoming-badge-${j.slug}`} className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.14em] font-semibold mb-2">
                          Upcoming Position · Accepting Applications
                        </span>
                      )}
                      <h2 className="font-heading text-xl text-slate-900 group-hover:text-[#3B597B]">{j.title}</h2>
                      {j.department && <p className="mt-1 text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">{j.department}</p>}
                      <p className="mt-3 text-slate-600 leading-relaxed">{j.summary}</p>
                      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600">
                        <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-slate-400" /> {j.location}</span>
                        <span className="inline-flex items-center gap-1.5"><Briefcase className="w-4 h-4 text-slate-400" /> {humanize(j.employment_type)} · {humanize(j.work_arrangement)}</span>
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-[#4A6FA5] shrink-0" />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}

        {/* General application invitation — always visible */}
        {jobs !== null && (
          <section className="mt-10 rounded-2xl border border-slate-200 bg-white p-6 md:p-8" data-testid="general-application-cta">
            <p className="text-xs uppercase tracking-[0.16em] text-[#4A6FA5] font-semibold">General Employment Application</p>
            <h2 className="mt-2 font-heading text-2xl text-slate-900">
              {jobs.length === 0 ? "Interested in future opportunities?" : "Don't see the right opportunity?"}
            </h2>
            <p className="mt-2 text-slate-600 leading-relaxed max-w-2xl">
              Submit a general employment application for future consideration. Tell us the employment types you'd consider — we'll keep your information on file and reach out if a matching opportunity opens.
            </p>
            <Link
              to="/careers/apply"
              data-testid="general-apply-cta"
              className="mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B]"
            >
              Submit a General Application <ArrowRight className="w-4 h-4" />
            </Link>
          </section>
        )}

        {/* Academy cross-link — professional development pathway */}
        {jobs !== null && (
          <section
            className="mt-6 rounded-2xl border border-slate-200 bg-gradient-to-br from-[#F0F4F8] to-white p-6 md:p-8"
            data-testid="academy-crosslink"
          >
            <div className="flex items-start gap-4">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white border border-slate-200 text-[#4A6FA5] shrink-0">
                <GraduationCap className="w-5 h-5" strokeWidth={1.75} />
              </div>
              <div className="flex-1">
                <p className="text-xs uppercase tracking-[0.16em] text-[#4A6FA5] font-semibold">
                  CCCHAPS Academy · Applications Open
                </p>
                <h2 className="mt-2 font-heading text-2xl text-slate-900">
                  Interested in becoming a workplace chaplain?
                </h2>
                <p className="mt-2 text-slate-600 leading-relaxed max-w-2xl">
                  Explore the CCCHAPS Chaplain Training Academy and apply to join our developing network of future workplace chaplains.
                </p>
                <Link
                  to="/academy"
                  data-testid="careers-academy-cta"
                  className="mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-white border border-[#4A6FA5] text-[#4A6FA5] text-sm font-medium hover:bg-[#4A6FA5] hover:text-white transition-colors"
                >
                  Explore the Academy <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
